// Utility functions and constants for the Advisor PAB pages
import { differenceInHours } from "date-fns";
import { parseDateInput } from "@/utils/formatDisplayDate";

/**
 * Normalise a raw PAB approval-status string into the canonical values used
 * across the advisor UI.
 *
 * "staged" is `SUGGESTED` + `is_selected=true` on the backend -- an advisor
 * the achiever has picked but who has NOT been sent anything: no token
 * minted, no email queued, no notification. It exists so a payment attempt
 * (successful or not) never has to re-run advisor selection, but it must
 * stay visibly distinct from "invited" -- collapsing the two previously made
 * a failed/pending payment show "You've invited N advisors. While you wait
 * for them to confirm..." to an achiever whose card had just been declined
 * and whose advisors have never heard from us. See
 * dispatch_staged_invites_after_payment (backend) for what actually flips
 * SUGGESTED -> INVITED.
 */
export const normalizeApprovalStatus = (
  approvalStatus?: string | null,
  fallbackStatus?: string | null,
): "staged" | "invited" | "accepted" | "rejected" | "expired" => {
  const normalized = (approvalStatus ?? fallbackStatus ?? "").toLowerCase();
  if (normalized === "suggested") return "staged";
  if (normalized === "accepted") return "accepted";
  if (normalized === "rejected" || normalized === "declined") return "rejected";
  if (normalized === "expired") return "expired";
  return "invited";
};

/**
 * Hours after which a PAB invite is considered expired on the frontend.
 * Mirrors INVITE_EXPIRY_HOURS = 48 in the backend.
 */
export const INVITE_EXPIRY_HOURS = 48;

/**
 * Computes the effective advisor status, detecting frontend-side expiry.
 *
 * The backend only flips approval_status to EXPIRED when the advisor tries to
 * accept a stale link. This helper lets the UI detect expiry proactively using
 * the board's `updated_at` timestamp (which the backend resets to "now" on every
 * invite / follow-up send), so the achiever sees the correct "Expired" badge and
 * Follow Up button without waiting for the advisor to click the link.
 */
export const getEffectiveAdvisorStatus = (
  approvalStatus?: string | null,
  lastInvitedAt?: string | null,
): "staged" | "invited" | "accepted" | "rejected" | "expired" => {
  const normalized = normalizeApprovalStatus(approvalStatus);
  if (normalized === "invited" && lastInvitedAt) {
    const inviteDate = parseDateInput(lastInvitedAt);
    if (inviteDate && differenceInHours(new Date(), inviteDate) >= INVITE_EXPIRY_HOURS) {
      return "expired";
    }
  }
  return normalized;
};

export interface OktStatusConfig {
  label: string;
  className: string;
}

export const OKT_STATUS_CONFIG: Record<string, OktStatusConfig> = {};

export const ADVISOR_BORDER_COLORS: string[] = [
  "var(--color-celebration-linkedin-border)",
  "var(--color-celebration-twitter-border)",
  "var(--color-celebration-copy-border)",
  "var(--color-celebration-pab-label)",
  "var(--color-goals-purple-selected)",
  "var(--color-profile-tab-active)",
  "var(--color-profile-icon-chip-bg)",
  "var(--color-profile-linked-in-icon-chip-bg)",
  "var(--color-profile-company-chip-bg)",
  "var(--color-profile-input-bg)",
  "var(--color-profile-input-border)",
  "var(--color-profile-skill-pill-bg)",
  "var(--color-profile-skill-pill-border)",
  "var(--color-profile-skill-pill-text)",
  "var(--color-profile-growth-pill-bg)",
  "var(--color-profile-growth-pill-border)",
  "var(--color-profile-growth-pill-text)",
  "var(--color-profile-availability-info-bg)",
  "var(--color-profile-availability-time-text)",
  "var(--color-profile-availability-day-bg)",
  "var(--color-profile-availability-typical-bg)",
];

export const getAdvisorBorderColor = (): string => {
  const randomIndex = Math.floor(
    Math.random() * ADVISOR_BORDER_COLORS.length
  );
  return ADVISOR_BORDER_COLORS[randomIndex];
};

export const shuffle = (array: string[]) => {
  return [...array].sort(() => Math.random() - 0.5);
};